import { useState, useEffect } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { AiConfig } from '@vedjegy/shared';

export default function AdminAiConfigPage() {
  const [config, setConfig]       = useState<AiConfig | null>(null);
  const [loading, setLoading]     = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveOk, setSaveOk]       = useState(false);
  const [error, setError]         = useState('');

  // Form state
  const [provider, setProvider]               = useState<AiConfig['provider']>('bedrock');
  const [modelId, setModelId]                 = useState('');
  const [fallbackModelId, setFallbackModelId] = useState('');
  const [enabled, setEnabled]                 = useState(false);

  useEffect(() => {
    api.aiConfig.get()
      .then((res) => {
        setConfig(res.config);
        setProvider(res.config.provider);
        setModelId(res.config.modelId);
        setFallbackModelId(res.config.fallbackModelId ?? '');
        setEnabled(res.config.enabled);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : '';
        if (msg.includes('403') || msg.includes('jogosultság')) setForbidden(true);
        else setError('Betöltési hiba.');
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true);
    setSaveOk(false);
    setError('');
    try {
      const payload: AiConfig = {
        provider,
        modelId,
        fallbackModelId: fallbackModelId.trim() || null,
        enabled,
      };
      await api.aiConfig.update(payload);
      setConfig(payload);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Mentési hiba.');
    } finally {
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', height: 36, padding: '0 10px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    fontSize: 13, fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
  };

  return (
    <AdminLayout>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 24 }}>AI konfiguráció</h1>

        {loading && <p style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Betöltés…</p>}

        {forbidden && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-lg)', padding: '16px 20px' }}>
            <p style={{ fontSize: 14, color: '#991b1b', margin: 0 }}>
              Nincs hozzáférésed ehhez az oldalhoz. Az AI konfiguráció csak az <code>AI_ADMIN_USER_IDS</code>-ben felsorolt felhasználók számára érhető el.
            </p>
          </div>
        )}

        {!loading && !forbidden && config !== null && (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '28px 32px', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Enabled toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 14, fontWeight: 600 }}>AI összefoglaló engedélyezve ennél a tenantnál</span>
            </label>

            {/* Provider */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Provider</label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as AiConfig['provider'])}
                style={{ ...inputStyle, height: 36, padding: '0 10px' }}
              >
                <option value="bedrock">AWS Bedrock</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
              </select>
            </div>

            {/* Primary Model ID */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Model ID (elsődleges)</label>
              <input
                type="text"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                placeholder="pl. anthropic.claude-sonnet-4-5-20251001-v2:0"
                style={inputStyle}
              />
              <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '4px 0 0' }}>
                Bedrock model ID — ez fut elsőként minden AI összefoglalónál.
              </p>
            </div>

            {/* Fallback Model ID */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Fallback Model ID{' '}
                <span style={{ fontWeight: 400, color: 'var(--muted-foreground)' }}>(opcionális)</span>
              </label>
              <input
                type="text"
                value={fallbackModelId}
                onChange={(e) => setFallbackModelId(e.target.value)}
                placeholder="pl. anthropic.claude-3-5-sonnet-20241022-v2:0"
                style={inputStyle}
              />
              <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '4px 0 0' }}>
                Ha az elsődleges model throttling vagy service hibával tér vissza, a rendszer ezzel próbálkozik újra.
              </p>
            </div>

            {error && (
              <p style={{ fontSize: 13, color: '#991b1b', margin: 0 }}>{error}</p>
            )}
            {saveOk && (
              <p style={{ fontSize: 13, color: '#166534', margin: 0 }}>Konfiguráció mentve.</p>
            )}

            <button
              onClick={() => void handleSave()}
              disabled={saving || !modelId.trim()}
              className="btn btn-primary"
              style={{ alignSelf: 'flex-start' }}
            >
              {saving ? 'Mentés…' : 'Mentés'}
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
